import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import { Loader2, AlertCircle, CheckCircle2, Clock, Hash } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface OverviewKpis {
  open_tickets: number;
  open_tickets_delta_pct: number;
  at_risk: number;
  at_risk_delta_pct: number;
  avg_first_response_min: number;
  avg_first_response_delta_pct: number;
  resolution_rate_pct: number;
  resolution_rate_delta_pct: number;
  volume_last_7_days: Array<{ day: string; count: number }>;
  status_breakdown: Array<{ status: string; count: number }>;
}

interface SlaReport {
  period: { from: string; to: string };
  total: number;
  met: number;
  breached: number;
  compliance_pct: number;
  by_priority: Array<{ priority: string; total: number; met: number; compliance_pct: number }>;
}

interface ChannelRow {
  channel: string;
  count: number;
}

interface AgentRow {
  agent_id: number;
  agent_name: string;
  total: number;
  resolved: number;
  open: number;
}

const PRIORITY_LABEL: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

const PRIORITY_COLOR: Record<string, string> = {
  urgent: '#e11d48',
  high: '#f97316',
  normal: '#64748b',
  low: '#94a3b8',
};

const STATUS_LABEL: Record<string, string> = {
  new: 'Nuevo',
  open: 'Abierto',
  in_progress: 'En proceso',
  waiting_customer: 'Esp. cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Correo',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  widget: 'Widget',
  api: 'API',
};

const CHANNEL_COLOR: Record<string, string> = {
  email: '#2563eb',
  whatsapp: '#10b981',
  telegram: '#0ea5e9',
  widget: '#6366f1',
  api: '#64748b',
};

export default function Reports() {
  const [overview, setOverview] = useState<OverviewKpis | null>(null);
  const [sla, setSla] = useState<SlaReport | null>(null);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch<OverviewKpis>('/reports/overview'),
      apiFetch<SlaReport>('/reports/sla'),
      apiFetch<ChannelRow[]>('/reports/by-channel'),
      apiFetch<AgentRow[]>('/reports/by-agent'),
    ])
      .then(([o, s, c, a]) => {
        setOverview(o);
        setSla(s);
        setChannels(c);
        setAgents(a);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando reportes…
      </div>
    );
  }

  if (error) {
    return <div className="py-10 text-center text-sm text-rose-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Reportes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Indicadores clave del servicio. Los datos se calculan en vivo desde la base.
        </p>
      </div>

      {/* Resumen general */}
      {overview && (
        <section>
          <h2 className="text-base font-semibold text-slate-900 mb-3">Resumen del momento</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi
              label="Tickets abiertos"
              value={overview.open_tickets}
              delta={overview.open_tickets_delta_pct}
              deltaInverse
              icon={<Hash size={16} />}
            />
            <Kpi
              label="En riesgo de SLA"
              value={overview.at_risk}
              delta={overview.at_risk_delta_pct}
              deltaInverse
              icon={<AlertCircle size={16} />}
              tone="warn"
            />
            <Kpi
              label="Tasa de resolución"
              value={`${overview.resolution_rate_pct}%`}
              delta={overview.resolution_rate_delta_pct}
              icon={<CheckCircle2 size={16} />}
              tone="ok"
            />
            <Kpi
              label="1ra respuesta promedio"
              value={`${overview.avg_first_response_min} min`}
              delta={overview.avg_first_response_delta_pct}
              deltaInverse
              icon={<Clock size={16} />}
            />
          </div>
        </section>
      )}

      {/* Volumen + status */}
      {overview && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Volumen de tickets (últimos 7 días)">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={overview.volume_last_7_days}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Tickets"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Distribución por estado">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={overview.status_breakdown.map((s) => ({
                  status: STATUS_LABEL[s.status] ?? s.status,
                  count: s.count,
                }))}
                layout="vertical"
                margin={{ left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} stroke="#94a3b8" width={110} />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </section>
      )}

      {/* SLA + Canales */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sla && (
          <Card
            title="Cumplimiento de SLA (últimos 30 días)"
            subtitle={`${sla.total} ticket${sla.total === 1 ? '' : 's'} cerrado${sla.total === 1 ? '' : 's'} en el período`}
          >
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="text-5xl font-bold text-slate-900">{sla.compliance_pct}%</div>
              <div className="text-xs text-slate-500">
                <span className="text-emerald-700 font-medium">{sla.met} cumplidos</span>
                {' · '}
                <span className="text-rose-700 font-medium">{sla.breached} vencidos</span>
              </div>
            </div>
            {sla.by_priority.length > 0 && (
              <div className="space-y-2 mt-2 border-t border-slate-100 pt-3">
                <div className="text-xs font-medium text-slate-600 mb-2">Por prioridad:</div>
                {sla.by_priority.map((p) => (
                  <div key={p.priority} className="flex items-center gap-2 text-xs">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: PRIORITY_COLOR[p.priority] }}
                    />
                    <span className="text-slate-700 w-16">{PRIORITY_LABEL[p.priority] ?? p.priority}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${p.compliance_pct}%`,
                          background: PRIORITY_COLOR[p.priority],
                        }}
                      />
                    </div>
                    <span className="text-slate-600 w-10 text-right">{p.compliance_pct}%</span>
                    <span className="text-slate-400 w-12 text-right">{p.met}/{p.total}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card title="Tickets por canal (últimos 7 días)">
          {channels.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              No hay tickets en los últimos 7 días.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={channels.map((c) => ({
                    name: CHANNEL_LABEL[c.channel] ?? c.channel,
                    value: c.count,
                    channel: c.channel,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  labelLine={false}
                >
                  {channels.map((c) => (
                    <Cell key={c.channel} fill={CHANNEL_COLOR[c.channel] ?? '#cbd5e1'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </section>

      {/* Por agente */}
      <section>
        <Card
          title="Carga por agente (últimos 30 días)"
          subtitle="Tickets asignados, resueltos y abiertos por agente"
        >
          {agents.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              No hay tickets asignados en los últimos 30 días.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agents}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="agent_name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resueltos" radius={[0, 0, 0, 0]} />
                <Bar dataKey="open" stackId="a" fill="#f59e0b" name="Abiertos" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  delta,
  deltaInverse = false,
  icon,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  delta?: number;
  /** Si true, valor que baja se considera bueno (ej. tickets abiertos). */
  deltaInverse?: boolean;
  icon: React.ReactNode;
  tone?: 'neutral' | 'ok' | 'warn';
}) {
  const toneClass =
    tone === 'ok' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-700';
  let deltaText: { text: string; class: string } | null = null;
  if (typeof delta === 'number' && Number.isFinite(delta) && delta !== 0) {
    const goingUp = delta > 0;
    const isPositive = deltaInverse ? !goingUp : goingUp;
    deltaText = {
      text: `${goingUp ? '+' : ''}${delta}% vs período anterior`,
      class: isPositive ? 'text-emerald-700' : 'text-rose-700',
    };
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">
          {label}
        </span>
        <span className={`${toneClass}`}>{icon}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {deltaText && <div className={`text-xs mt-1 ${deltaText.class}`}>{deltaText.text}</div>}
    </div>
  );
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
